import React from 'react';
import classNames from 'classnames';

import FocusTrap from 'focus-trap-react';

import isEmpty  from 'lodash/isEmpty.js';

import OverlayAssetViewport from './overlay_asset_viewport.jsx';
import { CloseIcon } from 'zoomforth_component_lib/external_modern_icons.jsx';
import { VisuallyHiddenSpan } from 'zoomforth_codesbane_viewer/component_lib/visually_hidden.jsx';

import SiteViewerVersionComponentSelector from 'zoomforth_codesbane_viewer/component_lib/site_viewer_version_component_selector_container.jsx';

const OverlayUi_ViewerVersion1 = ({
  slides,
  loading,
  sidebarTitle,
  sidebarDescription,
  displayFilename,
  htmlPackageEntrypointUrl,
  downloadLink,
  canDownloadAsset,
  assetLink,
  iframeEmbedUrl,
  iframeTitle,
  assetDisplayImage,
  assetDownloadLink,
  assetExtension,
  itemType,
  pptIsLoading,
  shouldRenderAsMobile,
  renderedTileCaption,
  videoishPlayerProps,
  className,
  tellRef,
  onLoadingChange,
  onPresentationSlideDisplay,
  onPresentationDownloadClick,
  onRequestClose,
  slideshowData,
}) => {
  
  const shouldDisplayCaption = (
    !isEmpty(slideshowData) ?
      (
        slideshowData.slideCaptionDisplay ||
        sidebarTitle ||
        sidebarDescription
      )
    :
      true
  );

  return (
    <div className={className} ref={tellRef}>
      <div className='overlay-background' onClick={onRequestClose} />

      <div className='overlay-box-wrapper'>
        <div
          className={classNames('overlay-asset-wrapper', {
            'full-width-override': !shouldDisplayCaption,
          })}
          onClick={onRequestClose}
        >
          {onRequestClose && (
            <div className='close-overlay-box'>
              <CloseIcon className='close-overlay-icon' />
            </div>
          )}
          <div className='clear'></div>

          <OverlayAssetViewport
            slides={slides}
            displayFilename={displayFilename}
            htmlPackageEntrypointUrl={htmlPackageEntrypointUrl}
            downloadLink={downloadLink}
            canDownloadAsset={canDownloadAsset}
            assetLink={assetLink}
            iframeEmbedUrl={iframeEmbedUrl}
            iframeTitle={iframeTitle}
            assetDisplayImage={assetDisplayImage}
            assetDownloadLink={assetDownloadLink}
            assetExtension={assetExtension}
            itemType={itemType}
            pptIsLoading={pptIsLoading}
            shouldRenderAsMobile={shouldRenderAsMobile}
            renderedTileCaption={renderedTileCaption}
            videoishPlayerProps={videoishPlayerProps}
            onLoadingChange={onLoadingChange}
            onPresentationSlideDisplay={onPresentationSlideDisplay}
            onPresentationDownloadClick={onPresentationDownloadClick}
          />
          {loading && (
            <div className='overlay-asset-loading'>
              <img src='https://d3jozdooylvm2p.cloudfront.net/images/gifs/loading.gif' />
            </div>
          )}
        </div>

        {shouldDisplayCaption && (
          <div className='overlay-caption-wrapper'>
            <div className='overlay-asset-title'>{sidebarTitle}</div>
            <div
              className='overlay-caption-content'
              dangerouslySetInnerHTML={{ __html: sidebarDescription }}
            ></div>
          </div>
        )}
      </div>
    </div>
  );
};

const OverlayUi_ViewerVersion2 = ({
  slides,
  loading,
  sidebarTitle,
  sidebarDescription,
  displayFilename,
  htmlPackageEntrypointUrl,
  downloadLink,
  canDownloadAsset,
  assetLink,
  iframeEmbedUrl,
  iframeTitle,
  assetDisplayImage,
  assetDownloadLink,
  assetExtension,
  itemType,
  altText,
  pptIsLoading,
  shouldRenderAsMobile,
  renderedTileCaption,
  videoishPlayerProps,
  className,
  tellRef,
  onLoadingChange,
  onPresentationSlideDisplay,
  onPresentationDownloadClick,
  onRequestClose,
  slideshowData,
  isPreviewCollaborationEnabled
}) => {
  const focusTrapOptions = {};
  if(isPreviewCollaborationEnabled){
    focusTrapOptions['clickOutsideDeactivates'] = true;
  }

  const shouldDisplayCaption = (
    !isEmpty(slideshowData) ?
      (
        slideshowData.slideCaptionDisplay ||
        sidebarTitle ||
        sidebarDescription
      )
    :
      true
  );

  return (
    <FocusTrap focusTrapOptions={focusTrapOptions}>
      <div
        className={className}
        ref={tellRef}
        role='dialog'
        aria-modal='true'
        aria-labelledby={sidebarTitle ? 'overlay-label-custom' : null}
        aria-label={sidebarTitle ? null : 'Asset overlay'}
      >
        <div className='overlay-background' onClick={onRequestClose} />
        <div className='overlay-box-wrapper'>
          <div
            className={classNames('overlay-asset-wrapper', {'full-width-override': !shouldDisplayCaption })}
            onClick={onRequestClose}
          >
            {onRequestClose && (
              <button
                role='button'
                className='close-overlay-box'
                onClick={onRequestClose}
              >
                <VisuallyHiddenSpan>Close</VisuallyHiddenSpan>
                <CloseIcon className='close-overlay-icon' />
              </button>
            )}

            <OverlayAssetViewport
              altText={altText}
              slides={slides}
              displayFilename={displayFilename}
              htmlPackageEntrypointUrl={htmlPackageEntrypointUrl}
              downloadLink={downloadLink}
              canDownloadAsset={canDownloadAsset}
              assetLink={assetLink}
              iframeEmbedUrl={iframeEmbedUrl}
              iframeTitle={iframeTitle}
              assetDisplayImage={assetDisplayImage}
              assetDownloadLink={assetDownloadLink}
              assetExtension={assetExtension}
              itemType={itemType}
              pptIsLoading={pptIsLoading}
              shouldRenderAsMobile={shouldRenderAsMobile}
              renderedTileCaption={renderedTileCaption}
              videoishPlayerProps={videoishPlayerProps}
              onLoadingChange={onLoadingChange}
              onPresentationSlideDisplay={onPresentationSlideDisplay}
              onPresentationDownloadClick={onPresentationDownloadClick}
            />

            {loading && (
              <div className='overlay-asset-loading'>
                <img
                  src='https://d3jozdooylvm2p.cloudfront.net/images/gifs/loading.gif'
                  alt='Loading icon'
                />
              </div>
            )}
          </div>

          {shouldDisplayCaption && (
            <div className='overlay-caption-wrapper'>
              <div id='overlay-label-custom' className='overlay-asset-title'>
                {sidebarTitle}
              </div>

              <div
                className='overlay-caption-content'
                dangerouslySetInnerHTML={{ __html: sidebarDescription }}
              />
            </div>
          )}
        </div>
      </div>
    </FocusTrap>
  );
};

const OverlayUi = (props) => (
  <SiteViewerVersionComponentSelector>
    <OverlayUi_ViewerVersion1 forSiteViewerVersion={1} {...props} />
    <OverlayUi_ViewerVersion2 forSiteViewerVersion={2} {...props} />
  </SiteViewerVersionComponentSelector>
);

export default OverlayUi;
